import { FC, useEffect, useRef, useState } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { GetPackageDetailsRequest } from 'ts/package/classes/GetPackageDetailsRequest';
import { getAnyPage, getBookDetails, updatePaymentBlock } from 'services/package';
import { AdditionalPayment, BookPackageResponse } from 'ts/package/interfaces/BookPackageResponse';
import PackageBookSkeleton from './PackageBookSkeleton';
import OrderBook from './OrderBook';
import BookSummary from './Summary';
import ExpiredToken from 'components/shared/ExpiredToken';
import usePageInMemory from 'hooks/usePageInMemory';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { CmsPageResponse } from 'ts/shared/interfaces/CmsPageResponse';
import { useGetPage } from 'hooks/useGetPage';
import { saveScreenshot } from 'utils/utils';

import { useTranslation } from 'react-i18next';
import Button from 'components/ui/Button/Button';
import { faArrowCircleLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import { b2b, isNetravel } from 'constants/generic';

const BookPackagePage: FC = () => {
  const [urlParams] = useSearchParams();
  const { t } = useTranslation();
  const [response, setResponse] = useState<BookPackageResponse>();
  const bookRef = useRef<HTMLDivElement>(null);
  
  const [totalPrice, setTotalPrice] = useState(0);
  const [couponAmount, setCouponAmount] = useState('');
  const [addTotal, setAddTotal] = useState(0);
  const { pathname } = useLocation();
  const [netravelPage, setNetravelPage] = useState<CmsPageResponse>();
  const [additionalPaymentsSelected, setAdditionalPaymentsSelected] = useState<AdditionalPayment[]>(
    []
  );
  const { i18n } = useTranslation();
  const location = useLocation();
  const idbKey = location.pathname + location.search;
  useGetPage('/ilpackages/book');
  const isTour = useRef(location.pathname.includes('tour')).current;
  const { onClickBack, onClickSearch } = usePageInMemory(idbKey, () => {});
  const packageDetailsParams = useRef<GetPackageDetailsRequest>();

  useEffect(() => {
    if (b2b) {
      if (location.pathname.toLowerCase() !== `/ilpackage/book`) {
        return;
      }
    }
    (async () => {
      const selectedRooms = urlParams.getAll('selectedRooms');
      const selectedInfantInRooms = urlParams.getAll('selectedInfantInRooms');
      const params = Object.fromEntries(urlParams);
      packageDetailsParams.current = new GetPackageDetailsRequest({
        ...params,
        selectedRooms,
        selectedInfantInRooms,
      });
      const response = await getBookDetails(packageDetailsParams.current);

      setTotalPrice(response?.bookingModel?.totalPrice || 0);

      setResponse(response);

      if (isNetravel) {
        const netravelPage = await getAnyPage({
          products: 'LDS',
          category: 'SystemComplex',
          systemProductsDetails: 'Booking',
        });
        setNetravelPage(netravelPage);
      }
    })();
  }, [pathname, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

  const onClickApply = async () => {
    const data = await updatePaymentBlock({
      packageCurrency: response?.bookingModel?.currency || '',
      sourcePrice: response?.bookingModel.credit2000PaymentInfo?.fullPriceInShekel || 0,
      addPrice: parseInt(couponAmount || '0') + addTotal,
      searchToken: response?.bookingModel.searchToken,
      lang: i18n.language,
    });

    if (!response?.bookingModel || !data) return;

    setResponse({
      ...response,
      bookingModel: {
        ...response.bookingModel,
        credit2000PaymentInfo: data.credit2000PaymentInfo,
      },
    });
  };

  const onChangeFacility = async (payments: AdditionalPayment[]) => {
    const totalAddPrice = payments.reduce((acc, p) => acc + p.addPrice, 0);
    const newTotalPrice = (response?.bookingModel?.totalPrice || 0) + totalAddPrice;

    setTotalPrice(newTotalPrice);
    setAddTotal(totalAddPrice);

    setAdditionalPaymentsSelected(payments);
    const data = await updatePaymentBlock({
      packageCurrency: response?.bookingModel?.currency || '',
      sourcePrice: response?.bookingModel.credit2000PaymentInfo?.fullPriceInShekel || 0,
      addPrice: parseInt(couponAmount || '0') + totalAddPrice,
      searchToken: response?.bookingModel.searchToken,
      lang: i18n.language,
    });

    if (!response?.bookingModel || !data) return;
    setResponse({
      ...response,
      bookingModel: {
        ...response.bookingModel,
        credit2000PaymentInfo: data.credit2000PaymentInfo,
      },
    });
  };

  return (
      <div className="details-wrapper" ref={bookRef}>
        {!!response?.bookingModel?.details && !response?.sessionExpired && (
          <div className="flex-column w100p">
            <div className="mb-20 default-shadow flex-between dyn-change-header">
              <Button
                title={t('back')}
                icon={faArrowCircleLeft}
                onClick={() => onClickBack(isTour ? 'tour' : 'ilpackage', 'details')}
                className="btn btn-link"
              />
              <Button
                title={t('search.new')}
                icon={faSearch}
                onClick={() =>
                  onClickSearch(
                    isTour ? 'tour' : 'ilpackage',
                    isTour ? '/tourssimple' : '/ilpackages'
                  )
                }
                className="btn btn-link"
              />
            </div>
            <div className="flex-between">
              <OrderBook
                bookingModel={response.bookingModel}
                onClickBook={async () => await saveScreenshot(bookRef.current, 'Book', 'Packages')}
                onChangeFacility={onChangeFacility}
                couponAmount={couponAmount}
                netravelPage={netravelPage}
                additionalPaymentsSelected={additionalPaymentsSelected}
              />
              <BookSummary
                bookingModel={response.bookingModel}
                totalPrice={totalPrice}
                couponAmount={couponAmount}
                setCouponAmount={setCouponAmount}
                onClickApply={onClickApply}
                netravelPage={netravelPage}
                additionalPaymentsSelected={additionalPaymentsSelected}
                packageDetailsParams={packageDetailsParams.current}
              />
            </div>
          </div>
        )}

        {response?.sessionExpired && <ExpiredToken storePath="ilpackage" />}

        {!response && <PackageBookSkeleton />}
      </div>
  );
};

export default withErrorBoundary(BookPackagePage, 'BookPackagePage');
